import { useMemo } from "react";

import { faBoltLightning } from "@fortawesome/pro-solid-svg-icons";
import { faChartBar } from "@fortawesome/pro-solid-svg-icons/faChartBar";
import { faFileExport } from "@fortawesome/pro-solid-svg-icons/faFileExport";
import { faMedal } from "@fortawesome/pro-solid-svg-icons/faMedal";
import kebabCase from "lodash-es/kebabCase";
import { defineMessages, IntlShape } from "react-intl";

import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import User from "@mapmycustomers/shared/types/User";

import Path from "@app/enum/Path";
import ReportType from "@app/enum/ReportType";
import { MenuCategory } from "@app/types/MenuItem";

import ReportCategory, { getCategoryMessagesDisplayName } from "./getCategoryMessagesDisplayName";
import getDashboardName from "./getDashboardName";

export const itemMessages = defineMessages({
  activity: {
    id: "reports.menu.item.activity",
    defaultMessage: "Activity",
    description: "Reports Menu item - Calendar Sync",
  },
  allCustomDashboard: {
    id: "reports.menu.item.customDashboards",
    defaultMessage: "All Custom Dashboards",
    description: "Reports Menu item - custom dashboards",
  },
  deals: {
    id: "reports.menu.item.deals",
    defaultMessage: "Deals",
    description: "Reports Menu item - Deals",
  },
  defaultDashboard: {
    id: "reports.menu.item.defaultDashboard",
    defaultMessage: "Default Dashboard",
    description: "Reports Menu item - default dashboard",
  },
  leaderboard: {
    id: "reports.menu.item.leaderboard",
    defaultMessage: "Stack Rank",
    description: "Reports Menu item - Leaderboard",
  },
  mileageTracking: {
    id: "reports.menu.item.mileageTracking",
    defaultMessage: "Mileage Tracker",
    description: "Reports Menu item - Mileage Tracking",
  },
  overview: {
    id: "reports.menu.item.overview",
    defaultMessage: "Overview",
    description: "Reports Menu item - Overview",
  },
  upgradeYourReports: {
    id: "reports.menu.item.upgradeYourReports",
    defaultMessage: "Upgrade Your Reports",
    description: "Reports Menu item - upgrade your reports",
  },
  weeklyScorecard: {
    id: "reports.menu.item.weeklyScorecard",
    defaultMessage: "Weekly Scorecard",
    description: "Reports Menu item - Weekly scorecard",
  },
});

const useMenu = (
  intl: IntlShape,
  users?: User[],
  isOwnerOrManager?: boolean,
  isReadOnlyMember?: boolean,
  isStackRankAvailable?: boolean,
  isCumulEnabled?: boolean,
  pinnedDashboards?: CumulDashboard[],
  canReadExports?: boolean
): MenuCategory[] => {
  const pinnedDashboardsDetails = useMemo(
    () =>
      (pinnedDashboards ?? [])?.map((dashboard, index) => {
        return {
          key: dashboard.name.en,
          label: getDashboardName(dashboard.name) as unknown as string,
          path: `${Path.REPORTS}/dashboard/${dashboard.id}`,
          uid: `dashboard-${index}`,
        };
      }) ?? [],
    [pinnedDashboards]
  );

  return useMemo(
    () => [
      ...(!isCumulEnabled
        ? isOwnerOrManager && users && users.length > 10
          ? [
              {
                icon: faBoltLightning,
                items: [
                  {
                    key: intl.formatMessage(itemMessages.upgradeYourReports),
                    label: intl.formatMessage(itemMessages.upgradeYourReports),
                    path: `${Path.REPORTS}/upgrade-your-reports`,
                    uid: "upgrade",
                  },
                ],
                name: getCategoryMessagesDisplayName(intl, ReportCategory.CUSTOM),
                uid: "custom",
              },
            ]
          : []
        : [
            {
              icon: faBoltLightning,
              items: [
                ...pinnedDashboardsDetails,
                {
                  key: intl.formatMessage(itemMessages.allCustomDashboard),
                  label: intl.formatMessage(itemMessages.allCustomDashboard),
                  path: `${Path.REPORTS}/all-dashboards`,
                  uid: "all-dashboards",
                },
              ],
              name: getCategoryMessagesDisplayName(intl, ReportCategory.CUSTOM),
              uid: "custom",
            },
          ]),
      {
        icon: faChartBar,
        items: [
          {
            key: intl.formatMessage(itemMessages.overview),
            label: intl.formatMessage(itemMessages.overview),
            path: `/reports/${ReportType.OVERVIEW}`,
            uid: "overview",
          },
          {
            key: intl.formatMessage(itemMessages.activity),
            label: intl.formatMessage(itemMessages.activity),
            path: `/reports/${ReportType.ACTIVITY}`,
            uid: "activities",
          },
          {
            key: intl.formatMessage(itemMessages.deals),
            label: intl.formatMessage(itemMessages.deals),
            path: `/reports/${ReportType.DEALS}`,
            uid: "deals",
          },
          {
            key: intl.formatMessage(itemMessages.mileageTracking),
            label: intl.formatMessage(itemMessages.mileageTracking),
            path: `/reports/${kebabCase(ReportType.MILEAGE_TRACKING)}`,
            uid: "mileage-tracking",
          },
        ],
        name: getCategoryMessagesDisplayName(intl, ReportCategory.GENERAL),
        uid: "general",
      },

      {
        icon: faMedal,
        items: [
          ...(isStackRankAvailable
            ? [
                {
                  key: intl.formatMessage(itemMessages.leaderboard),
                  label: intl.formatMessage(itemMessages.leaderboard),
                  path: `/reports/stack-rank`,
                  uid: "stack-rank",
                },
              ]
            : []),
          {
            key: intl.formatMessage(itemMessages.weeklyScorecard),
            label: intl.formatMessage(itemMessages.weeklyScorecard),
            path: `/reports/weekly-report-card`,
            uid: "weekly-report",
          },
        ],
        name: getCategoryMessagesDisplayName(intl, ReportCategory.PERFORMANCE),
        uid: "performance",
      },
      ...(isReadOnlyMember || !canReadExports
        ? []
        : [
            {
              icon: faFileExport,
              items: undefined,
              name: getCategoryMessagesDisplayName(intl, ReportCategory.EXPORTS),
              path: `/reports/${ReportType.EXPORTS}`,
              uid: "exports",
            },
          ]),
    ],
    [
      canReadExports,
      intl,
      isCumulEnabled,
      isOwnerOrManager,
      isReadOnlyMember,
      isStackRankAvailable,
      pinnedDashboardsDetails,
      users,
    ]
  );
};

export default useMenu;
